// =============================================================================
// campaign-client.http.ts — Adaptateur HTTP RÉEL du CampaignClient (S11, D4).
// =============================================================================
// Remplace le stub-first S10 derrière le MÊME DI seam (CampaignClientProvider) : tous les
// écrans/hooks consomment l'interface CampaignClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, withTenant, estampillage catalog_version_id,
// transitions, validité des cycles, K-anonymity). Cet adaptateur ne porte AUCUNE règle métier :
// il sérialise l'intention, parse la réponse, et MAPPE les erreurs.
//
// SÉCURITÉ (RULE_REF_006) : le bearer/tenant ne sont JAMAIS fabriqués ni stockés ici. La session
// admin est portée par le COOKIE httpOnly posé par l'API (credentials: 'include') — l'UI ne lit ni
// n'écrit le secret de session. Aucune donnée métier en localStorage.
//
// DISCRIMINATEUR 409 (D1) : le corps 409 est STRUCTURÉ `{ statusCode, code, message }`. On lit
// `code` et on le mappe vers un CampaignApiErrorKind via CAMPAIGN_409_CODE_TO_KIND (table figée) —
// l'écran affiche le message métier explicite, jamais un crash.

import {
  CAMPAIGN_409_CODE_TO_KIND,
  CampaignApiError,
  type AbandonCampaignInput,
  type AbandonCampaignResultDto,
  type AddClientScopeInput,
  type AddCycleInput,
  type CycleNumberDto,
  type AddParticipationInput,
  type AddProfileScopeInput,
  type AddProfileScopeBatchInput,
  type ApplyTargetPriceDefaultsResultDto,
  type CampaignClient,
  type CampaignDetailDto,
  type CampaignListItemDto,
  type CatalogTreeNodeDto,
  type ClientScopeDto,
  type CloneCampaignInput,
  type CloseCampaignResultDto,
  type CloseCycleInput,
  type CloseCycleResultDto,
  type CreateCampaignInput,
  type CycleScheduleDto,
  type OpenCampaignC2ResultDto,
  type OpenCampaignResultDto,
  type ProfileScopeDto,
  type SetCampaignDatesInput,
  type SetTargetPriceConfigInput,
  type SupplierParticipationDto,
  type TargetPriceConfigDto,
  type TargetPriceDefaultsDto,
} from '@/api/campaign-client';

/** Forme attendue du corps 409 structuré (discriminateur D1). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/**
 * Forme WIRE NESTED renvoyée par GET /admin/campaigns/:id (API CampaignDetailView) : la campagne
 * sous la clé `campaign` + les tableaux frères. APLATIE en CampaignDetailDto (plat) par getCampaign.
 * `targetPrices` est servi par l'API mais NON consommé par l'écran de détail (omis du DTO UI plat).
 */
interface CampaignDetailWireView {
  readonly campaign: CampaignListItemDto & {
    readonly createdBy: string;
    readonly lastModifiedAt: string;
    // V-CREATE / F0-d — dates globales + provenance de duplication (portées par CampaignView côté API).
    readonly startDate: string | null;
    readonly endDate: string | null;
    readonly clonedFromIntitule: string | null;
    readonly clonedFromDate: string | null;
  };
  // B4 (GATE_HUMAN 2026-09-15) — flag de diffusion des noms de clients (champ FRÈRE de `campaign` côté API).
  readonly clientsDiffusionEnabled: boolean;
  readonly clientScopes: readonly ClientScopeDto[];
  readonly profileScopes: readonly ProfileScopeDto[];
  readonly participations: readonly SupplierParticipationDto[];
  readonly cycles: readonly CycleScheduleDto[];
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine).
 * `undefined` si absent (pas de session, ou contexte sans document) — l'appel mutant partira
 * alors sans en-tête et l'API répondra 403 (fail-closed côté serveur, jamais d'écriture).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : effectue les appels `fetch` contre l'API admin campaign. Le `baseUrl`
 * par défaut est vide (chemins relatifs : la SPA et l'API partagent l'origine, ou un proxy/reverse
 * proxy route `/admin/*`). En test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpCampaignClient implements CampaignClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  // --- Helpers réseau --------------------------------------------------------

  /** Construit l'URL absolue (baseUrl + chemin relatif racine API). */
  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Exécute une requête et renvoie le JSON typé. En cas d'erreur HTTP, lève une CampaignApiError :
   * sur 409, lit le `code` du corps structuré et le mappe vers un `kind` (discriminateur D1) ;
   * sinon, relaie le message serveur tel quel (autorité API).
   *
   * CSRF DOUBLE-SUBMIT : sur les méthodes MUTANTES (POST/PUT/PATCH/DELETE), l'AdminSessionGuard
   * exige l'en-tête `x-csrf-token` = valeur du cookie NON-httpOnly `admin_csrf` (posé au login).
   * Le SPA le lit (même origine) et le ré-émet. Sans cet en-tête, toute écriture admin est 403
   * (« Jeton CSRF absent ou non concordant »). Le secret de session (httpOnly) n'est JAMAIS lu ici.
   */
  private async request<T>(
    path: string,
    init: RequestInit,
    expectBody: boolean,
  ): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }

    const response = await fetch(this.url(path), {
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });

    if (!response.ok) {
      throw await this.toApiError(response);
    }
    if (!expectBody) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse en erreur applicative typée (mapping 409 code→kind). */
  private async toApiError(response: Response): Promise<CampaignApiError> {
    const body = await this.safeJson(response);
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la campagne.`;

    if (response.status === 409 && typeof body?.code === 'string') {
      // DISCRIMINATEUR 409 : code machine stable → kind UI (table figée). Code inconnu = 'generic'.
      const kind = CAMPAIGN_409_CODE_TO_KIND[body.code] ?? 'generic';
      return new CampaignApiError(message, kind, response.status);
    }
    return new CampaignApiError(message, undefined, response.status);
  }

  /** Parse le corps JSON sans jeter (réponses d'erreur non-JSON tolérées). */
  private async safeJson(
    response: Response,
  ): Promise<StructuredErrorBody | undefined> {
    try {
      return (await response.json()) as StructuredErrorBody;
    } catch {
      return undefined;
    }
  }

  private get(path: string): Promise<unknown> {
    return this.request(path, { method: 'GET' }, true);
  }

  private post<T>(path: string, payload: unknown, expectBody = true): Promise<T> {
    return this.request<T>(
      path,
      { method: 'POST', body: JSON.stringify(payload) },
      expectBody,
    );
  }

  private delete(path: string): Promise<void> {
    return this.request<void>(path, { method: 'DELETE' }, false);
  }

  // --- Lectures --------------------------------------------------------------

  public async listCampaigns(): Promise<readonly CampaignListItemDto[]> {
    return (await this.get('/admin/campaigns')) as CampaignListItemDto[];
  }

  public async getCampaign(campaignId: string): Promise<CampaignDetailDto> {
    // L'API renvoie une vue agrégée NESTED `{ campaign: CampaignView, cycles, clientScopes,
    // profileScopes, participations, targetPrices }` (CampaignDetailView). Le DTO UI
    // (CampaignDetailDto) est PLAT : champs de la campagne au premier niveau + tableaux frères.
    // On APLATIT ici (un-nest `campaign`) — sans cette adaptation, `campaign.state` serait
    // `undefined` et l'écran de détail planterait (lookup de libellé/variant sur undefined).
    const view = (await this.get(
      `/admin/campaigns/${encodeURIComponent(campaignId)}`,
    )) as CampaignDetailWireView;
    return {
      ...view.campaign,
      // B4 — le flag est un champ FRÈRE de `campaign` dans la vue API (fail-closed false côté service).
      clientsDiffusionEnabled: view.clientsDiffusionEnabled,
      clientScopes: view.clientScopes,
      profileScopes: view.profileScopes,
      participations: view.participations,
      cycles: view.cycles,
    };
  }

  public async listCatalogTree(): Promise<readonly CatalogTreeNodeDto[]> {
    return (await this.get('/admin/campaigns/catalog-tree')) as CatalogTreeNodeDto[];
  }

  // --- Création / clonage ----------------------------------------------------

  public async createCampaign(
    input: CreateCampaignInput,
  ): Promise<CampaignListItemDto> {
    return this.post<CampaignListItemDto>('/admin/campaigns', input);
  }

  public async cloneCampaign(
    sourceCampaignId: string,
    input: CloneCampaignInput,
  ): Promise<CampaignListItemDto> {
    return this.post<CampaignListItemDto>(
      `/admin/campaigns/${encodeURIComponent(sourceCampaignId)}/clone`,
      input,
    );
  }

  // --- Périmètre client ------------------------------------------------------

  public async addClientScope(
    campaignId: string,
    input: AddClientScopeInput,
  ): Promise<ClientScopeDto> {
    return this.post<ClientScopeDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/client-scopes`,
      input,
    );
  }

  public async removeClientScope(
    campaignId: string,
    scopeId: string,
  ): Promise<void> {
    await this.delete(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/client-scopes/${encodeURIComponent(scopeId)}`,
    );
  }

  public async setClientsDiffusion(
    campaignId: string,
    enabled: boolean,
  ): Promise<void> {
    // B4 (GATE_HUMAN 2026-09-15) — POST { enabled } ; l'API répond 204 (aucun corps attendu).
    await this.post<void>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/clients-diffusion`,
      { enabled },
      false,
    );
  }

  // --- Périmètre profil ------------------------------------------------------

  public async addProfileScope(
    campaignId: string,
    input: AddProfileScopeInput,
  ): Promise<ProfileScopeDto> {
    return this.post<ProfileScopeDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/profile-scopes`,
      input,
    );
  }

  public async addProfileScopeBatch(
    campaignId: string,
    input: AddProfileScopeBatchInput,
  ): Promise<readonly ProfileScopeDto[]> {
    return this.post<ProfileScopeDto[]>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/profile-scopes/batch`,
      input,
    );
  }

  public async removeProfileScope(
    campaignId: string,
    scopeId: string,
  ): Promise<void> {
    await this.delete(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/profile-scopes/${encodeURIComponent(scopeId)}`,
    );
  }

  // --- Participations fournisseurs -------------------------------------------

  public async addParticipation(
    campaignId: string,
    input: AddParticipationInput,
  ): Promise<SupplierParticipationDto> {
    return this.post<SupplierParticipationDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/participations`,
      input,
    );
  }

  public async removeParticipation(
    campaignId: string,
    participationId: string,
  ): Promise<void> {
    await this.delete(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/participations/${encodeURIComponent(participationId)}`,
    );
  }

  // --- Planning des cycles ---------------------------------------------------

  public async addCycle(
    campaignId: string,
    input: AddCycleInput,
  ): Promise<CycleScheduleDto> {
    return this.post<CycleScheduleDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/cycles`,
      input,
    );
  }

  public async removeCycle(
    campaignId: string,
    cycleNumber: CycleNumberDto,
  ): Promise<void> {
    await this.delete(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/cycles/${String(cycleNumber)}`,
    );
  }

  public async setCampaignDates(
    campaignId: string,
    input: SetCampaignDatesInput,
  ): Promise<CampaignListItemDto> {
    return this.post<CampaignListItemDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/dates`,
      input,
    );
  }

  // --- Prix Cibles & marges (S11) --------------------------------------------

  public async listTargetPriceConfigs(
    campaignId: string,
  ): Promise<readonly TargetPriceConfigDto[]> {
    return (await this.get(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/target-prices`,
    )) as TargetPriceConfigDto[];
  }

  public async setTargetPriceConfig(
    campaignId: string,
    input: SetTargetPriceConfigInput,
  ): Promise<TargetPriceConfigDto> {
    return this.post<TargetPriceConfigDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/target-prices`,
      input,
    );
  }

  public async listTargetPriceDefaults(
    campaignId: string,
  ): Promise<TargetPriceDefaultsDto> {
    return (await this.get(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/target-prices/defaults`,
    )) as TargetPriceDefaultsDto;
  }

  public async applyTargetPriceDefaults(
    campaignId: string,
  ): Promise<ApplyTargetPriceDefaultsResultDto> {
    // Corps VIDE : la pose groupée cible la campagne par son id d'URL. 409 LOCKED / BUSY mappés par toApiError.
    return this.post<ApplyTargetPriceDefaultsResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/target-prices/apply-defaults`,
      {},
    );
  }

  // --- Planification (« Planifier le paramétrage », E-3b-1) ------------------

  public async planCampaign(
    campaignId: string,
  ): Promise<CampaignListItemDto> {
    // Corps VIDE (la planification cible la campagne par son id d'URL). 200 CampaignView (plat) ;
    // 409 structuré CAMPAIGN_NOT_READY_TO_PLAN / CAMPAIGN_PLAN_WRONG_STATE mappé par toApiError.
    return this.post<CampaignListItemDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/plan`,
      {},
    );
  }

  // --- Ouverture (« Lancement », S11) ----------------------------------------

  public async openCampaign(
    campaignId: string,
  ): Promise<OpenCampaignResultDto> {
    // Corps VIDE (l'ouverture cible la campagne par son id d'URL + l'admin authentifié).
    return this.post<OpenCampaignResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/open`,
      {},
    );
  }

  // --- Pilotage des clôtures / ouverture C2 / abandon (S15) ------------------

  public async closeCycle(
    campaignId: string,
    input: CloseCycleInput,
  ): Promise<CloseCycleResultDto> {
    // EARLY-CLOSE admin : corps = {cycleNumber}. 200 CloseCycleResultView ; 409 structuré
    // CAMPAIGN_CLOSE_WRONG_STATE / CAMPAIGN_CLOSE_UNKNOWN_CYCLE mappé par toApiError.
    return this.post<CloseCycleResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/close-cycle`,
      input,
    );
  }

  public async openCampaignC2(
    campaignId: string,
  ): Promise<OpenCampaignC2ResultDto> {
    // Corps VIDE (l'ouverture C2 cible la campagne par son id d'URL). 200 OpenCampaignC2ResultView ;
    // 409 structuré (_WRONG_STATE / _EMPTY_* / _NO_VALIDATED_TARGET_PRICE / _NO_PLANNED_CYCLE).
    return this.post<OpenCampaignC2ResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/open-c2`,
      {},
    );
  }

  public async abandonCampaign(
    campaignId: string,
    input: AbandonCampaignInput,
  ): Promise<AbandonCampaignResultDto> {
    // Abandon IRRÉVERSIBLE : corps {reason:{motif, freeText?}}. 200 AbandonCampaignResultView ;
    // 409 CAMPAIGN_ABANDON_ALREADY_TERMINAL ; 422 motif hors enum (relayé generic par toApiError).
    return this.post<AbandonCampaignResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/abandon`,
      input,
    );
  }

  public async closeCampaign(
    campaignId: string,
  ): Promise<CloseCampaignResultDto> {
    // Clôture DÉFINITIVE (UC-A-052) : corps VIDE (finalCycle DÉRIVÉ de l'état côté API). 200
    // CloseCampaignResultView ; 409 structuré CAMPAIGN_FINAL_CLOSE_WRONG_STATE mappé par toApiError.
    return this.post<CloseCampaignResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/close`,
      {},
    );
  }
}
